import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { useUnreadMailCount } from '../../hooks/useUnreadMailCount';

// タップアニメーション用スタイル
const tapAnimStyle = `
  @keyframes navTap {
    0%   { transform: scale(1); }
    35%  { transform: scale(0.78); }
    65%  { transform: scale(1.18); }
    85%  { transform: scale(0.95); }
    100% { transform: scale(1); }
  }
  @keyframes navGlow {
    0%   { opacity: 0; transform: scale(0.6); }
    50%  { opacity: 1; transform: scale(1.4); }
    100% { opacity: 0; transform: scale(1.8); }
  }
  @keyframes divePulse {
    0%, 100% { box-shadow: 0 0 20px rgba(0,217,255,0.6), 0 0 40px rgba(236,72,153,0.4); }
    50% { box-shadow: 0 0 30px rgba(0,217,255,0.9), 0 0 60px rgba(236,72,153,0.7); }
  }
  .nav-tap-anim { animation: navTap 0.38s cubic-bezier(0.36,0.07,0.19,0.97) both; }
  .nav-glow-anim { animation: navGlow 0.45s ease-out forwards; }
  .dive-pulse { animation: divePulse 2s ease-in-out infinite; }
`;

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userType, setUserType] = useState<string | null>(null);
  const [tappedItem, setTappedItem] = useState<string | null>(null);

  // 未読メール数（リアルタイム）
  const unreadMailCount = useUnreadMailCount(user?.id);

  const isActive = (path: string) => location.pathname === path;

  const handleNavTap = (key: string) => {
    setTappedItem(key);
    setTimeout(() => setTappedItem(null), 400);
  };

  useEffect(() => {
    if (!user) { setUserType(null); return; }
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.id)
        .maybeSingle();
      if (data) { setUserType(data.user_type); }
    };
    fetchProfile();
  }, [user]);

  // ── 新5タブ構成 ──
  const navItems = [
    {
      to: '/explore',
      label: 'EXPLORE',
      icon: 'ri-compass-3-line',
      activeColor: 'text-neon-cyan',
      activeBg: 'bg-neon-cyan/20 border-neon-cyan shadow-[0_0_14px_rgba(0,217,255,0.35)]',
      glowColor: 'rgba(0,217,255,0.6)',
    },
    {
      to: '/timeline',
      label: 'ECHOES',
      icon: 'ri-sound-module-line',
      activeColor: 'text-[#ff8c00]',
      activeBg: 'bg-[#ff8c00]/20 border-[#ff8c00] shadow-[0_0_14px_rgba(255,140,0,0.35)]',
      glowColor: 'rgba(255,140,0,0.6)',
    },
    {
      to: '/scan',
      label: 'SCAN',
      icon: 'ri-scan-2-line',
      activeColor: 'text-neon-cyan',
      activeBg: 'bg-gradient-to-br from-neon-cyan/30 to-neon-purple/30 border-neon-cyan',
      glowColor: 'rgba(0,217,255,0.8)',
      isSpecial: true, // 中央の特別ボタン
    },
    {
      to: '/ranking',
      label: 'RANKING',
      icon: 'ri-trophy-line',
      activeColor: 'text-yellow-400',
      activeBg: 'bg-yellow-400/20 border-yellow-400 shadow-[0_0_14px_rgba(250,204,21,0.35)]',
      glowColor: 'rgba(250,204,21,0.6)',
    },
    {
      to: '/mypage',
      label: 'DASHBOARD',
      icon: 'ri-dashboard-3-line',
      activeColor: 'text-neon-green',
      activeBg: 'bg-neon-green/20 border-neon-green shadow-[0_0_14px_rgba(0,255,65,0.35)]',
      glowColor: 'rgba(0,255,65,0.6)',
      requireAuth: true,
    },
  ];

  const handleNavClick = (item: typeof navItems[0], e: React.MouseEvent) => {
    handleNavTap(item.to);
    
    // ログインが必要な項目で未ログインの場合
    if (item.requireAuth && !user) {
      e.preventDefault();
      navigate('/login');
    }
  };

  return (
    <>
      <style>{tapAnimStyle}</style>

      {/* ── Desktop Navigation ── */}
      <nav className="hidden lg:block fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-t border-white/10 z-40">
        <div className="container mx-auto px-8">
          <div className="flex items-end justify-center gap-golden-lg py-golden-sm">

            {navItems.map((item) => {
              if (item.isSpecial) {
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={(e) => handleNavClick(item, e)}
                    className="flex flex-col items-center gap-golden-xs transition-all duration-300 group cursor-pointer -mt-6"
                  >
                    <div className="relative">
                      {tappedItem === item.to && (
                        <span
                          className="nav-glow-anim absolute inset-0 rounded-full pointer-events-none"
                          style={{ background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)` }}
                        />
                      )}
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center border-2 transition-all dive-pulse ${
                          isActive(item.to)
                            ? 'bg-gradient-to-br from-neon-cyan/40 to-pink-500/40 border-neon-cyan'
                            : 'bg-gradient-to-br from-neon-cyan/30 to-pink-500/30 border-neon-cyan/70'
                        } ${tappedItem === item.to ? 'nav-tap-anim' : ''}`}
                      >
                        <i className={`${item.icon} text-3xl ${isActive(item.to) ? 'text-neon-cyan' : 'text-white'}`}></i>
                      </div>
                    </div>
                    <span className={`text-golden-xs tracking-widest font-bold whitespace-nowrap ${
                      isActive(item.to) ? 'text-neon-cyan' : 'text-gray-400'
                    }`}>{item.label}</span>
                  </Link>
                );
              }

              const isDashboard = item.to === '/mypage';
              const showBadge = isDashboard && user && unreadMailCount > 0;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => handleNavClick(item, e)}
                  className={`flex flex-col items-center gap-golden-xs transition-colors duration-300 group cursor-pointer ${
                    isActive(item.to) ? item.activeColor : 'text-gray-500 hover:text-white'
                  }`}
                >
                  <div className="relative">
                    {tappedItem === item.to && (
                      <span
                        className="nav-glow-anim absolute inset-0 rounded-full pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)` }}
                      />
                    )}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all ${
                        isActive(item.to)
                          ? item.activeBg
                          : 'bg-white/5 border-white/10 group-hover:bg-white/10 group-hover:border-white/20'
                      } ${tappedItem === item.to ? 'nav-tap-anim' : ''}`}
                    >
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    {showBadge && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[9px] font-black text-black px-1 leading-none"
                        style={{ background: '#00d9ff', boxShadow: '0 0 8px rgba(0,217,255,0.7)' }}
                      >
                        {unreadMailCount > 99 ? '99+' : unreadMailCount}
                      </span>
                    )}
                  </div>
                  <span className="text-golden-xs tracking-widest font-bold whitespace-nowrap">{item.label}</span>
                </Link>
              );
            })}

          </div>
        </div>
      </nav>

      {/* ── Mobile Navigation ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-t border-white/10 z-40">
        <div className="flex items-end justify-around px-golden-sm py-golden-sm">

          {navItems.map((item) => {
            if (item.isSpecial) {
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => handleNavClick(item, e)}
                  className="flex flex-col items-center gap-golden-xxs transition-all duration-300 cursor-pointer -mt-8"
                >
                  <div className="relative">
                    {tappedItem === item.to && (
                      <span
                        className="nav-glow-anim absolute inset-0 rounded-full pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)` }}
                      />
                    )}
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center border-2 transition-all dive-pulse ${
                        isActive(item.to)
                          ? 'bg-gradient-to-br from-neon-cyan/40 to-pink-500/40 border-neon-cyan'
                          : 'bg-gradient-to-br from-neon-cyan/30 to-pink-500/30 border-neon-cyan/70'
                      } ${tappedItem === item.to ? 'nav-tap-anim' : ''}`}
                    >
                      <i className={`${item.icon} text-2xl ${isActive(item.to) ? 'text-neon-cyan' : 'text-white'}`}></i>
                    </div>
                  </div>
                  <span className={`text-[9px] tracking-widest font-bold whitespace-nowrap ${
                    isActive(item.to) ? 'text-neon-cyan' : 'text-gray-400'
                  }`}>{item.label}</span>
                </Link>
              );
            }

            const isDashboard = item.to === '/mypage';
            const showBadge = isDashboard && user && unreadMailCount > 0;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={(e) => handleNavClick(item, e)}
                className={`flex flex-col items-center gap-golden-xxs transition-colors duration-300 cursor-pointer ${
                  isActive(item.to) ? item.activeColor : 'text-gray-500'
                }`}
              >
                <div className="relative">
                  {tappedItem === item.to && (
                    <span
                      className="nav-glow-anim absolute inset-0 rounded-full pointer-events-none"
                      style={{ background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)` }}
                    />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all ${
                      isActive(item.to)
                        ? item.activeBg
                        : 'bg-white/5 border-white/10'
                    } ${tappedItem === item.to ? 'nav-tap-anim' : ''}`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  {showBadge && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center rounded-full text-[8px] font-black text-black px-0.5 leading-none"
                      style={{ background: '#00d9ff', boxShadow: '0 0 6px rgba(0,217,255,0.7)' }}
                    >
                      {unreadMailCount > 99 ? '99+' : unreadMailCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] tracking-widest font-bold whitespace-nowrap">{item.label}</span>
              </Link>
            );
          })}

        </div>
      </nav>
    </>
  );
}